import { Loader } from "shared/components/Loader";
import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import {
  ApplicantStatus,
  ApplicantType,
  Verification,
  VerificationNames
} from "shared/types";
import { StartApplicationSplash } from "./slices/StartApplicationSplash";
import { useApplicant } from "./hooks";
import { ManualApplicationFlow } from "./components/ManualApplicationFlow";

export const ManualApplication = ({
  setOpen,
  applicationId,
  applicantId
}: {
  setOpen: (open: boolean) => void;
  applicationId?: number;
  applicantId?: number;
}) => {
  const verificationsMeta = useQuery({
    queryKey: ["verificationsMeta", String(applicationId), String(applicantId)],
    queryFn: () =>
      instance
        .get<{
          verifications: Verification[];
        }>(
          `/portal/v1/applications/${applicationId}/verifications?applicant_id=${applicantId}`
        )
        .then((res) => {
          return res.data;
        }),
    enabled: Boolean(applicationId) && Boolean(applicantId)
  });

  const {
    applicant,
    isApplicationLoading,
    isApplicationRefreshing,
    refetchApplication,
    application
  } = useApplicant({
    applicationId,
    applicantId
  });

  if (isApplicationLoading)
    return (
      <div className="h-full flex items-center justify-center animate-appear">
        <Loader />
      </div>
    );

  if (applicant?.application_status === ApplicantStatus.Pending)
    return (
      <StartApplicationSplash
        applicantFullName={`${applicant.first_name} ${applicant.last_name}`}
        applicantId={Number(applicantId)}
        applicationId={Number(applicationId)}
        onSuccess={() => {
          refetchApplication();
          verificationsMeta.refetch();
        }}
        onClose={() => setOpen(false)}
      />
    );

  if (!applicant || !applicationId || !application) return null;

  const submissionDisclosureJsonSchema =
    verificationsMeta.data?.verifications.find(
      (verification) =>
        verification.name === VerificationNames.SubmissionDisclosure
    )?.steps?.[0].json_schema as
      | { partner_name_in_submission_disclosure?: string }
      | undefined;

  return (
    <ManualApplicationFlow
      isApplicationRefreshing={isApplicationRefreshing}
      refetchApplication={refetchApplication}
      setOpen={setOpen}
      partnerNameInSubmissionDisclosure={
        submissionDisclosureJsonSchema?.partner_name_in_submission_disclosure ||
        application.partner_name_in_submission_disclosure
      }
      applicant={applicant}
      verificationsMeta={verificationsMeta}
      application={application}
      requiredYearsHousing={
        application.verification_template.verification_schemas?.[
          applicant.role || ApplicantType.Applicant
        ]?.[VerificationNames.HousingHistory]
          ?.min_required_continuous_rental_history || 2
      }
    />
  );
};
